import db from 'src/db';
import {
  Notification,
  NotificationType,
  NotificationKind,
  DEFAULT_KIND_BY_TYPE,
  DEFAULT_ACTION_URL_BY_TYPE,
  NOTIFICATION_KIND_CONFIG,
  mapLegacyToNotificationType,
} from '../types/ModernNotification';
import { LCName, getItemLC } from '../../localstorage';
import { Timestamp as Tstamp } from 'firebase/firestore';
import * as customerServ from '../../../../db/serviceCustomer';
import { UserCompanies } from '../../../models/User';
import * as Log from '../../../../util/SDayslogger';

export interface NotificationQueryOptions {
  limit?: number;
  hoursAgo?: number;
  userId?: string;
  companyId?: string;
  unreadOnly?: boolean;
}

export class NotificationRepository {
  private static instance: NotificationRepository;
  private companyAccesses: UserCompanies[] = [];

  constructor() {
    this.companyAccesses = getItemLC(LCName.CompanyList) ?? [];
  }

  static getInstance(): NotificationRepository {
    if (!this.instance) {
      this.instance = new NotificationRepository();
    }
    return this.instance;
  }

  private calculateStartDate(hoursAgo: number): Date {
    const currentDate = new Date();
    const startDate = new Date(currentDate);
    startDate.setHours(startDate.getHours() - hoursAgo);
    return startDate;
  }

  /**
   * Convert legacy Firebase document to new Notification format
   */
  private async convertFirebaseToNotification(doc: any): Promise<Notification | null> {
    try {
      const data = doc.data();
      const notificationId = doc.id;

      // Get customer data if available
      let customerData = null;
      if (data?.customer?.id) {
        try {
          customerData = await customerServ.get(data.customer.id);
        } catch (error) {
          console.warn(`Could not fetch customer ${data.customer.id}:`, error);
        }
      }

      // Get company data
      const company = this.companyAccesses.find(comp => comp.id === data.company?.id);

      // Convert timestamp
      const timestamp = data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date();

      // Map legacy type to new NotificationType
      const notificationType = mapLegacyToNotificationType(data.type, data.status);
      const kind = DEFAULT_KIND_BY_TYPE[notificationType];
      const kindConfig = NOTIFICATION_KIND_CONFIG[kind];

      // Build notification based on type
      const notification = this.buildNotificationByType(
        notificationId,
        notificationType,
        kind,
        kindConfig,
        data,
        customerData,
        company,
        timestamp
      );

      return notification;
    } catch (error) {
      Log.error('Error converting Firebase notification:', error);
      return null;
    }
  }

  /**
   * Build notification object based on type
   */
  private buildNotificationByType(
    id: string,
    type: NotificationType,
    kind: NotificationKind,
    kindConfig: { icon: string; iconColor: string },
    data: any,
    customerData: any,
    company: UserCompanies | undefined,
    timestamp: Date
  ): Notification {
    const customerName = customerData?.firstName || data.customerProfileName || 'Customer';
    const read = data.notifiedAt ? true : false;

    const baseNotification: Notification = {
      id,
      type,
      kind,
      title: '',
      message: '',
      timestamp,
      read,
      icon: kindConfig.icon,
      iconColor: kindConfig.iconColor,
      actionUrl: DEFAULT_ACTION_URL_BY_TYPE[type],
      channel: data.channel,
      collaborationId: data.collaboration?.id,
      customerId: customerData?.id,
      companyId: company?.id,
      companyName: company?.companyName,
      phoneNumber: data.customerPhoneNumber || customerData?.phoneNumber,
    };

    // Customize based on type
    switch (type) {
      case 'NEW_MESSAGE':
        return {
          ...baseNotification,
          title: customerName,
          message: data.message || 'New message received',
          icon: 'chat',
          senderName: customerName,
          roomId: data.room?.id,
        };

      case 'MESSAGE_FROM_TEAMMATE':
        return {
          ...baseNotification,
          title: 'Team Message',
          message: `Message sent by teammate in ${company?.companyName || 'shared conversation'}`,
          icon: 'group',
        };

      case 'COLLABORATION_QUEUED':
        return {
          ...baseNotification,
          title: 'New Collaboration',
          message: `${customerName} is waiting in queue via ${data.channel || 'unknown channel'}`,
          icon: 'person_add',
        };

      case 'COLLABORATION_ASSIGNED':
        return {
          ...baseNotification,
          title: 'Collaboration Assigned',
          message: `Collaboration with ${customerName} has been assigned`,
          icon: 'assignment_ind',
        };

      case 'SYNC_COMPLETED':
        const syncRes = data.syncRes || data.orders;
        return {
          ...baseNotification,
          title: 'Sync Completed',
          message: syncRes
            ? `${syncRes.length || 0} orders synchronized`
            : 'Orders synchronized successfully',
          icon: 'sync',
          actionUrl: '/orders',
        };

      case 'SYNC_FAILED':
        return {
          ...baseNotification,
          title: 'Sync Failed',
          message: data.message || 'Failed to sync orders',
          icon: 'sync_problem',
        };

      case 'ORDER_NEW':
      case 'ORDER_PAID':
      case 'ORDER_SHIPPED':
      case 'ORDER_COMPLETED':
      case 'ORDER_CANCELLED':
        return {
          ...baseNotification,
          title: this.getOrderTitle(type),
          message: data.message || `Order update from ${data.marketplace || 'marketplace'}`,
          orderId: data.orderId,
          actionUrl: data.orderId ? `/orders/${data.orderId}` : '/orders',
        };

      case 'TOKEN_EXPIRED':
        return {
          ...baseNotification,
          title: 'Token Expired',
          message: data.message || 'Please reconnect your marketplace account',
          icon: 'warning',
          actionUrl: '/settings/accounts',
        };

      case 'SYSTEM_ALERT':
      default:
        return {
          ...baseNotification,
          title: data.title || 'Notification',
          message: data.message || data.textContent || 'You have a new notification',
          icon: 'notifications',
        };
    }
  }

  private getOrderTitle(type: NotificationType): string {
    switch (type) {
      case 'ORDER_NEW': return 'New Order';
      case 'ORDER_PAID': return 'Order Paid';
      case 'ORDER_SHIPPED': return 'Order Shipped';
      case 'ORDER_COMPLETED': return 'Order Completed';
      case 'ORDER_CANCELLED': return 'Order Cancelled';
      default: return 'Order Update';
    }
  }

  /**
   * Get notifications for a user from Firebase
   */
  async getNotifications(options: NotificationQueryOptions = {}): Promise<Notification[]> {
    const {
      limit = 50,
      hoursAgo = 168, // 7 days default (matches TTL)
      userId,
      unreadOnly = false
    } = options;

    if (!userId) {
      console.error('NotificationRepository: userId is required');
      return [];
    }

    try {
      const startDate = this.calculateStartDate(hoursAgo);

      let query = db
        .collection('users')
        .doc(userId)
        .collection('notifications')
        .where('createdAt', '>=', Tstamp.fromDate(startDate))
        .orderBy('createdAt', 'desc')
        .limit(limit);

      if (unreadOnly) {
        query = query.where('notifiedAt', '==', null);
      }

      const querySnapshot = await query.get();

      if (querySnapshot.docs.length === 0) {
        return [];
      }

      const notifications: Notification[] = [];

      const results = await Promise.allSettled(
        querySnapshot.docs.map(doc => this.convertFirebaseToNotification(doc))
      );

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          notifications.push(result.value);
        }
      });

      return notifications.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    } catch (error) {
      console.error('NotificationRepository: Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read in Firebase
   */
  async markAsRead(userId: string, notificationId: string): Promise<boolean> {
    try {
      await db
        .collection('users')
        .doc(userId)
        .collection('notifications')
        .doc(notificationId)
        .update({
          notifiedAt: new Date(),
        });

      return true;
    } catch (error) {
      Log.error('Error marking notification as read:', error);
      return false;
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId: string, hoursAgo: number = 168): Promise<boolean> {
    try {
      const startDate = this.calculateStartDate(hoursAgo);
      const querySnapshot = await db
        .collection('users')
        .doc(userId)
        .collection('notifications')
        .where('createdAt', '>=', Tstamp.fromDate(startDate))
        .where('notifiedAt', '==', null)
        .get();

      const batch = db.batch();
      querySnapshot.docs.forEach(doc => {
        batch.update(doc.ref, { notifiedAt: new Date() });
      });

      await batch.commit();
      return true;
    } catch (error) {
      Log.error('Error marking all notifications as read:', error);
      return false;
    }
  }

  /**
   * Delete notification from Firebase
   */
  async deleteNotification(userId: string, notificationId: string): Promise<boolean> {
    try {
      await db
        .collection('users')
        .doc(userId)
        .collection('notifications')
        .doc(notificationId)
        .delete();

      return true;
    } catch (error) {
      Log.error('Error deleting notification:', error);
      return false;
    }
  }

  /**
   * Subscribe to real-time notifications
   */
  subscribeToNotifications(
    userId: string,
    callback: (notifications: Notification[]) => void,
    options: NotificationQueryOptions = {}
  ): () => void {
    const { hoursAgo = 168, limit = 50 } = options;
    const startDate = this.calculateStartDate(hoursAgo);

    const unsubscribe = db
      .collection('users')
      .doc(userId)
      .collection('notifications')
      .where('createdAt', '>=', Tstamp.fromDate(startDate))
      .orderBy('createdAt', 'desc')
      .limit(limit)
      .onSnapshot(
        async (querySnapshot) => {
          try {
            const notifications: Notification[] = [];

            for (const change of querySnapshot.docChanges()) {
              if (change.type === 'added' || change.type === 'modified') {
                const notification = await this.convertFirebaseToNotification(change.doc);
                if (notification) {
                  notifications.push(notification);
                }
              }
            }

            if (notifications.length > 0) {
              callback(notifications);
            }
          } catch (error) {
            Log.error('Error processing notification subscription:', error);
          }
        },
        (error) => {
          Log.error('Error in notification subscription:', error);
        }
      );

    return unsubscribe;
  }

  /**
   * Get unread count for user
   */
  async getUnreadCount(userId: string, hoursAgo: number = 168): Promise<number> {
    try {
      const startDate = this.calculateStartDate(hoursAgo);
      const querySnapshot = await db
        .collection('users')
        .doc(userId)
        .collection('notifications')
        .where('createdAt', '>=', Tstamp.fromDate(startDate))
        .where('notifiedAt', '==', null)
        .get();

      return querySnapshot.size;
    } catch (error) {
      Log.error('Error getting unread count:', error);
      return 0;
    }
  }
}

export const notificationRepository = NotificationRepository.getInstance();
